import PropTypes from 'prop-types';
import { useState } from "react";
import * as Sentry from "@sentry/react";
import { apiFetch } from "../utility/apiFetch.js";
import RangeSlider from 'react-range-slider-input';
import SpinnerButton from '../components/SpinnerButton.jsx';

const gaugeBlue = "#1d4ed8";  // info token — below-range zone
const gaugeGreen = "#16a34a"; // success token — in-range zone
const gaugeRed = "#dc2626";   // danger token — above-range zone

const PHASES = [
  { key: "inoculation", label: "Inoculation" },
  { key: "spawning",    label: "Spawning" },
  { key: "pinning",     label: "Pinning" },
  { key: "fruiting",    label: "Fruiting" },
  { key: "harvest",     label: "Harvest" },
];

function trackGradient(values, min, max) {
  const lo = ((values[0] - min) / (max - min)) * 100;
  const hi = ((values[1] - min) / (max - min)) * 100;
  return `linear-gradient(to right, ${gaugeBlue} ${lo}%, ${gaugeGreen} ${lo}%, ${gaugeGreen} ${hi}%, ${gaugeRed} ${hi}%)`;
}

function phaseFromDB(phaseData) {
  return {
    temperature: [phaseData?.temperature?.low_value ?? 70, phaseData?.temperature?.high_value ?? 80],
    humidity:    [phaseData?.humidity?.low_value    ?? 60, phaseData?.humidity?.high_value    ?? 100],
    co2:         [phaseData?.co2?.low_value          ?? 0,  phaseData?.co2?.high_value          ?? 2000],
  };
}

export default function SettingsThresholds({ thresholds }) {
  const dbPhases = thresholds?.phases ?? {};
  const initialActivePhase = thresholds?.active_phase || "fruiting";

  const [activePhase, setActivePhase] = useState(initialActivePhase);
  const [sliders, setSliders] = useState(() =>
    Object.fromEntries(PHASES.map(p => [p.key, phaseFromDB(dbPhases[p.key])]))
  );
  const [unsavedPhases, setUnsavedPhases] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const s = sliders[activePhase] ?? phaseFromDB(undefined);

  const newTempValues = (values) => {
    setSliders(prev => ({ ...prev, [activePhase]: { ...prev[activePhase], temperature: values } }));
    setUnsavedPhases(prev => new Set(prev).add(activePhase));
  };
  const newHumidityValues = (values) => {
    setSliders(prev => ({ ...prev, [activePhase]: { ...prev[activePhase], humidity: values } }));
    setUnsavedPhases(prev => new Set(prev).add(activePhase));
  };
  const newCo2Values = (values) => {
    setSliders(prev => ({ ...prev, [activePhase]: { ...prev[activePhase], co2: values } }));
    setUnsavedPhases(prev => new Set(prev).add(activePhase));
  };

  const resetSettings = () => {
    setSliders(prev => ({ ...prev, [activePhase]: phaseFromDB(dbPhases[activePhase]) }));
    setUnsavedPhases(prev => {
      const next = new Set(prev);
      next.delete(activePhase);
      return next;
    });
  };

  const saveSettings = async (setAsActive) => {
    setLoading(true);
    try {
      await apiFetch(`thresholds/${activePhase}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          thresholds: {
            temperature: { low_value: s.temperature[0], high_value: s.temperature[1] },
            humidity:    { low_value: s.humidity[0],    high_value: s.humidity[1] },
            co2:         { low_value: s.co2[0],         high_value: s.co2[1] },
          },
          set_active: setAsActive,
        }),
      });
      setUnsavedPhases(prev => {
        const next = new Set(prev);
        next.delete(activePhase);
        return next;
      });
    } catch (err) {
      Sentry.captureException(err);
      setError(err.message);
    }
    setLoading(false);
  };

  const isUnsaved = unsavedPhases.has(activePhase);
  const isActive = activePhase === initialActivePhase;

  return (
    <div className="container w-full rounded-xl bg-white dark:bg-gray-800 shadow px-4 py-8 min-h-40">
      <style>{`
        .range-slider-temp { background: ${trackGradient(s.temperature, 40, 100)} !important; }
        .range-slider-humidity { background: ${trackGradient(s.humidity, 0, 100)} !important; }
        .range-slider-co2 { background: ${trackGradient(s.co2, 0, 12000)} !important; }
      `}</style>

      <h2 className="text-brand dark:text-brand-onDark text-2xl font-semibold sm:py-2">
        Sensor targets
      </h2>

      {/* Phase tabs */}
      <div className="flex gap-1 mt-4 mb-6 border-b border-gray-200 dark:border-gray-700">
        {PHASES.map(p => {
          const isTab = p.key === activePhase;
          const isDirty = unsavedPhases.has(p.key);
          const isCurrentActive = p.key === initialActivePhase;
          return (
            <button
              key={p.key}
              onClick={() => setActivePhase(p.key)}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors
                ${isTab
                  ? "bg-white dark:bg-gray-800 border border-b-white dark:border-b-gray-800 border-gray-200 dark:border-gray-700 -mb-px text-brand dark:text-brand-onDark"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}
            >
              {p.label}
              {isCurrentActive && (
                <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-brand dark:bg-brand-onDark align-middle" title="Active phase" />
              )}
              {isDirty && (
                <span className="ml-1 text-warning dark:text-warning-onDark text-xs">●</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex">
        <div className="flex-1 text-gray-700 dark:text-gray-200">
          <span className="">
            Temperature:
          </span>
          <RangeSlider
            min={40}
            max={100}
            value={s.temperature}
            onInput={newTempValues}
            className="range-slider-temp range-slider-tall"
          />
        </div>
        <div className="w-[200px] p-4 text-gray-700 dark:text-gray-200">
          {s.temperature[0]} &rarr; {s.temperature[1]} &deg;F
        </div>
      </div>
      <div className="flex">
        <div className="flex-1 text-gray-700 dark:text-gray-200">
          Humidity: <RangeSlider
            min={0}
            max={100}
            value={s.humidity}
            onInput={newHumidityValues}
            className="range-slider-humidity"
          />
        </div>
        <div className="w-[200px] p-4 text-gray-700 dark:text-gray-200">
          {s.humidity[0]} &rarr; {s.humidity[1]} %RH
        </div>
      </div>
      <div className="flex">
        <div className="flex-1 text-gray-700 dark:text-gray-200">
          CO2: <RangeSlider
            min={0}
            max={12000}
            step={100}
            value={s.co2}
            onInput={newCo2Values}
            className="range-slider-co2"
          />
        </div>
        <div className="w-[200px] p-4 text-gray-700 dark:text-gray-200">
          {s.co2[0]} &rarr; {s.co2[1]} PPM
        </div>
      </div>

      {isUnsaved && (
        <div className="mt-4">
          {loading ? (
            <SpinnerButton />
          ) : (<>
            <button
              className="bg-brand dark:bg-brand-fillOnDark hover:bg-brand-hover dark:hover:bg-brand-dark px-4 py-2 rounded text-white font-bold uppercase tracking-wide"
              onClick={() => saveSettings(isActive)}>
              Save
            </button>
            {!isActive && (
              <button
                className="ml-2 bg-brand-hover dark:bg-brand-dark hover:bg-brand-dark px-4 py-2 rounded text-white font-bold uppercase tracking-wide"
                onClick={() => saveSettings(true)}>
                Save + Set Active
              </button>
            )}
            <button
              className="ml-2 bg-warning hover:bg-amber-700 px-4 py-2 rounded text-white font-bold uppercase tracking-wide"
              onClick={resetSettings}>
              Discard
            </button>
          </>)}
        </div>
      )}
    </div>
  );
}

SettingsThresholds.propTypes = {
  thresholds: PropTypes.shape({
    active_phase: PropTypes.string,
    phases: PropTypes.object,
  }).isRequired,
};
